import {
    IconButton,
    Avatar,
    Box,
    CloseButton,
    Flex,
    HStack,
    VStack,
    Icon,
    useColorModeValue,
    Text,
    Drawer,
    DrawerContent,
    useDisclosure,
    BoxProps,
    FlexProps,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
} from '@chakra-ui/react'
import {
    FiHome,
    FiSettings,
    FiMenu,
    FiChevronDown,
    FiClock,
    FiBook,
} from 'react-icons/fi'
import { IconType } from 'react-icons'
import React, { useEffect, useState } from 'react';
import { directus } from '../api';
import { readMe, readAssetRaw } from '@directus/sdk';
import { useAuth } from '../context';
import { User } from '../interfaces';
import { Home, Reminders, CustomerPortal, ReminderRules } from './subpages/mainpage';
interface LinkItemProps {
    name: string
    icon: IconType
    returnFunction: () => JSX.Element
}

interface NavItemProps extends FlexProps {
    icon: IconType
    path: string
    children: React.ReactNode
}

interface MobileProps extends FlexProps {
    onOpen: () => void
}

interface SidebarProps extends BoxProps {
    onClose: () => void
}

const NormalizedLinkName = (linkName: string) => {
    return linkName.toLowerCase().replace(' ', '_')
}

const LinkItems: Array<LinkItemProps> = [
    { name: 'Home', icon: FiHome, returnFunction: () => { return <Home /> } },
    { name: 'Reminders', icon: FiClock, returnFunction: () => { return <Reminders /> } },
    { name: 'Reminder Rules', icon: FiSettings, returnFunction: () => { return <ReminderRules /> } },
    { name: 'Customers Portal', icon: FiBook, returnFunction: () => { return <CustomerPortal /> } },
    // { name: 'Settings', icon: FiSettings }, setup settings later on
]

const SidebarContent = ({ onClose, ...rest }: SidebarProps) => {
    return (
        <Box
            transition="3s ease"
            bg={useColorModeValue('white', 'gray.900')}
            borderRight="1px"
            borderRightColor={useColorModeValue('gray.200', 'gray.700')}
            w={{ base: 'full', md: 60 }}
            pos="fixed"
            h="full"
            {...rest}>
            <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
                <Text fontSize="2xl" fontFamily="monospace" fontWeight="bold" color="teal">
                    RaphaRecord
                </Text>
                <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
            </Flex>
            {LinkItems.map((link) => (
                <NavItem key={link.name} icon={link.icon} path={('#' + NormalizedLinkName(link.name))}>
                    {link.name}
                </NavItem>
            ))}
        </Box>
    )
}

const NavItem = ({ icon, path, children, ...rest }: NavItemProps) => {
    return (
        <Box
            as="a"
            href={path}
            style={{ textDecoration: 'none' }}
            _focus={{ boxShadow: 'none' }}>
            <Flex
                align="center"
                p="4"
                mx="4"
                borderRadius="lg"
                role="group"
                cursor="pointer"
                _hover={{
                    bg: 'cyan.400',
                    color: 'white',
                }}
                {...rest}>
                {icon && (
                    <Icon
                        mr="4"
                        fontSize="16"
                        _groupHover={{
                            color: 'white',
                        }}
                        as={icon}
                    />
                )}
                {children}
            </Flex>
        </Box>
    )
}


async function getCurrentUser() {
    try {
        const currentUser = await directus.request(readMe());
        return currentUser as User;
    } catch (error) {
        console.error('Error fetching current user:', error);
        throw error;
    }
}

async function streamToBlob(stream: ReadableStream): Promise<Blob> {
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
    }

    return new Blob(chunks);
}

const MobileNav = ({ onOpen, ...rest }: MobileProps) => {
    const auth = useAuth();
    const [name, setName] = useState("RaphaRecord");
    const [image, setImage] = useState("/logo512.png");
    const [email, setEmail] = useState("default@rapharecord.com")
    useEffect(() => {
        const fetchData = async () => {
            try {
                const userData = await getCurrentUser();
                if (userData) {
                    if (userData.first_name && userData.last_name && userData.email) {
                        setName(userData.first_name + " " + userData.last_name);
                        if (userData.avatar) {
                            const imageFile = await directus.request((readAssetRaw as any)(userData.avatar));
                            if (imageFile instanceof ReadableStream) {
                                const blob = await streamToBlob(imageFile);
                                const url = URL.createObjectURL(blob);
                                setImage(url);
                            }
                        }
                        setEmail(userData.email);
                    }
                } else {
                    console.log('First name not available');
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        fetchData(); // Call the async function
    }, []); // Empty dependency array to run once on component mount
    return (
        <Flex
            ml={{ base: 0, md: 60 }}
            px={{ base: 4, md: 4 }}
            height="20"
            alignItems="center"
            bg={useColorModeValue('white', 'gray.900')}
            borderBottomWidth="1px"
            borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
            justifyContent={{ base: 'space-between', md: 'flex-end' }}
            {...rest}>
            <IconButton
                display={{ base: 'flex', md: 'none' }}
                onClick={onOpen}
                variant="outline"
                aria-label="open menu"
                icon={<FiMenu />}
            />

            <Text
                display={{ base: 'flex', md: 'none' }}
                fontSize="2xl"
                fontFamily="monospace"
                fontWeight="bold"
                color="teal">
                RaphaRecord
            </Text>

            <HStack spacing={{ base: '0', md: '6' }}>
                <Flex alignItems={'center'}>
                    <Menu>
                        <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: 'none' }}>
                            <HStack>
                                <Avatar
                                    size={'sm'}
                                    src={image}
                                />
                                <VStack
                                    display={{ base: 'none', md: 'flex' }}
                                    alignItems="flex-start"
                                    spacing="1px"
                                    ml="2">
                                    <Text fontSize="sm">{name}</Text>
                                    <Text fontSize="xs" color="gray.600">
                                        {email}
                                    </Text>
                                </VStack>
                                <Box display={{ base: 'none', md: 'flex' }}>
                                    <FiChevronDown />
                                </Box>
                            </HStack>
                        </MenuButton>
                        <MenuList
                            bg={useColorModeValue('white', 'gray.900')}
                            borderColor={useColorModeValue('gray.200', 'gray.700')}>
                            <MenuItem onClick={auth.logOut}>Log out</MenuItem>
                        </MenuList>
                    </Menu>
                </Flex>
            </HStack>
        </Flex>
    )
}

const MainPage = () => {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [hash, setHash] = useState('#home')
    useEffect(() => {
        // make sure to link to hash on system change
        if (window.location.hash !== "") {
            setHash(NormalizedLinkName(window.location.hash));
        }
        
        // handle change events
        const handleHashChange = () => {
            setHash(window.location.hash)
        };
        // Add event listener
        window.addEventListener('hashchange', handleHashChange);

        // Clean up
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);
    return (
        <Box minH="100vh" bg={useColorModeValue('gray.100', 'gray.900')}>
            <SidebarContent onClose={() => onClose} display={{ base: 'none', md: 'block' }} />
            <Drawer
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                returnFocusOnClose={false}
                onOverlayClick={onClose}
                size="full">
                <DrawerContent>
                    <SidebarContent onClose={onClose} />
                </DrawerContent>
            </Drawer>
            {/* mobilenav */}
            <MobileNav onOpen={onOpen} />
            <Box ml={{ base: 0, md: 60 }} p="4">
                {
                    LinkItems.map((link) => (
                        ("#" + NormalizedLinkName(link.name)) === NormalizedLinkName(hash)) ? link.returnFunction() : <></>
                    )
                }
            </Box>
        </Box>
    )
}

export default MainPage